import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AppCard from "../components/AppCard";
import { fetchApplications } from "../services/appStoreApi";
import { featuredClientSlugs, withAppVisuals } from "../content/appVisuals";
import "../styles/universeAppStore.css";

export default function ApplicationStorePage() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [activeCategory, setActiveCategory] = useState("ALL");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPageError("");
    fetchApplications()
      .then((items) => { if (active) setApplications(items.map(withAppVisuals)); })
      .catch((error) => { if (active) setPageError(error.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [retryCount]);

  const featuredApps = useMemo(() => applications.filter((app) => featuredClientSlugs.includes(app.slug)).slice(0, 3), [applications]);

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return applications.filter((app) =>
      (activeCategory === "ALL" || app.category === activeCategory) &&
      (!search || app.name.toLowerCase().includes(search) ||
        app.shortDescription?.toLowerCase().includes(search))
    );
  }, [applications, activeCategory, query]);

  return (
    <main className="uas-shell">
      <section className="uas-hero">
        <div className="uas-hero__content">
          <p className="uas-eyebrow">Universe application center</p>
          <h1>Universe apps, all in one place.</h1>
          <p>Discover Android tools for point of sale, business activity, barcode scanning and future staff workspaces.</p>
        </div>
        <div className="uas-hero__status">
          <div><AppsRoundedIcon /><span><strong>{loading ? "—" : applications.length}</strong> applications in the catalog</span></div>
          <div><ShieldOutlinedIcon /><span><strong>Official releases</strong> Available when published</span></div>
        </div>
      </section>
      {featuredApps.length > 0 && (
        <section className="uas-feature-showcase">
          <div className="uas-section-heading uas-section-heading--tight">
            <div>
              <p className="uas-eyebrow">Featured apps</p>
              <h2>Explore the main mobile tools.</h2>
            </div>
            <span>Direct preview pages</span>
          </div>
          <div className="uas-featured-grid">
            {featuredApps.map((app) => (
              <button
                key={app.slug}
                type="button"
                className="uas-featured-card"
                onClick={() => navigate(`/store/universe-mobile/application/${app.slug}`)}
              >
                <div className="uas-featured-card__copy">
                  <p className="uas-eyebrow">{app.heroLabel || "Universe app"}</p>
                  <h3>{app.marketingTitle || app.name}</h3>
                  <p>{app.marketingBody || app.shortDescription}</p>
                  <span className="uas-featured-card__cta">Open preview</span>
                </div>
                {app.featuredImage && (
                  <div className="uas-featured-card__media">
                    <img src={app.featuredImage} alt={`${app.name} showcase`} />
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="uas-store-panel">
        <div className="uas-toolbar">
          <div className="uas-tabs" role="group" aria-label="Application categories">
            {[["ALL", "All apps"], ["CLIENT", "Client apps"], ["STAFF", "Staff apps"]].map(([value, label]) => (
              <button type="button" key={value} className={activeCategory === value ? "is-active" : ""}
                aria-pressed={activeCategory === value} onClick={() => setActiveCategory(value)}>{label}</button>
            ))}
          </div>
          <label className="uas-search">
            <SearchRoundedIcon /><input aria-label="Search applications" value={query}
              onChange={(event) => setQuery(event.target.value)} placeholder="Search applications" />
          </label>
        </div>
        {loading && <div className="uas-state">Loading official applications…</div>}
        {pageError && <div className="uas-state uas-state--error" role="alert">{pageError}
          <button type="button" onClick={() => setRetryCount((value) => value + 1)}>Try again</button>
        </div>}
        {!loading && !pageError && <>
          <div className="uas-section-heading">
            <div><p className="uas-eyebrow">Browse the ecosystem</p>
              <h2>{activeCategory === "STAFF" ? "Staff applications" : activeCategory === "CLIENT" ? "Client applications" : "Explore applications"}</h2>
            </div>
            <span>{filtered.length} {filtered.length === 1 ? "app" : "apps"}</span>
          </div>
          <div className="uas-app-grid">
            {filtered.map((app) => <AppCard key={app.slug} app={app}
              onOpen={() => navigate(`/store/universe-mobile/application/${app.slug}`)} />)}
          </div>
          {!filtered.length && <div className="uas-state">No applications match your search.</div>}
        </>}
      </section>
    </main>
  );
}
